import { forwardRef } from 'react';
import { HiMinus, HiOutlineCheck, HiPlus } from 'react-icons/hi';
import { getNumberFormat } from '../../../../utils/getNumberFormat';

// One selectable modifier / main-option chip. Content-sized so a Wrap of
// pills flows like the kiosk: every pill is a single line, never a grid cell
// that forces the name to break ("Choco/late").
//
// Quantity mode (group.allowquantities): an unselected pill adds one unit on
// tap; once selected it grows an embedded [−] n [+] stepper and its price
// becomes the extended price (unit × units). The body of a selected quantity
// pill is inert — only the two buttons act, so a stray tap can't over-add.
const OptionPill = forwardRef(
  (
    {
      active,
      onClick,
      disabled,
      label,
      price,
      soldOut,
      kcal,
      qty = 0,
      onDecrement,
      onIncrement,
      incrementDisabled,
    },
    ref,
  ) => {
    const stepper = active && !soldOut && qty > 0 && !!onDecrement;
    const shownPrice = stepper ? (price || 0) * qty : price;

    const stepButton = (Icon, ariaLabel, handler, isDisabled) => (
      <button
        type="button"
        aria-label={ariaLabel}
        disabled={isDisabled}
        onClick={(e) => {
          e.stopPropagation();
          if (!isDisabled) handler();
        }}
        className="grid place-items-center"
        style={{
          width: '28px',
          height: '28px',
          borderRadius: '999px',
          border: 'none',
          background: isDisabled
            ? 'rgba(255, 255, 255, 0.10)'
            : 'rgba(255, 255, 255, 0.22)',
          color: isDisabled ? 'rgba(255, 255, 255, 0.45)' : '#FFFFFF',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          flexShrink: 0,
          padding: 0,
          transition: 'background 0.15s',
        }}
      >
        <Icon size={14} />
      </button>
    );

    return (
      <div
        ref={ref}
        role={stepper ? undefined : 'button'}
        onClick={disabled || stepper ? undefined : onClick}
        // The pill always fills its cell: a full-width row below sm, one of
        // three equal grid columns from sm up. Name left, price/stepper right;
        // the name never breaks across lines (it ellipsizes if it must). The
        // stepper is sized so "Strawberry +$0.75 [-] 1 [+]" fits a 213px
        // column — the modal's third at desktop width.
        className="select-none flex w-full items-center gap-1.5 max-w-full transition-all"
        style={{
          padding: stepper ? '4px 4px 4px 12px' : '10px 12px',
          borderRadius: '12px',
          fontSize: '13px',
          fontWeight: 600,
          lineHeight: 1.3,
          background: active
            ? 'var(--ui-ink)'
            : soldOut
              ? 'var(--ui-surface-2)'
              : 'var(--ui-surface)',
          color: active ? '#FFFFFF' : 'var(--ui-ink)',
          border: active
            ? '1px solid var(--ui-ink)'
            : soldOut
              ? '1px solid var(--ui-line)'
              : '1px solid var(--ui-line-2)',
          boxShadow: active
            ? '0 0 0 3px var(--accent-soft, var(--site-color-10))'
            : 'none',
          cursor: stepper ? 'default' : disabled ? 'not-allowed' : 'pointer',
          opacity: soldOut ? 1 : disabled && !active ? 0.45 : 1,
          minHeight: '48px',
          transform: 'scale(1)',
        }}
        onMouseDown={(e) => {
          if (disabled || stepper) return;
          e.currentTarget.style.transform = 'scale(0.97)';
        }}
        onMouseUp={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        <span
          className="min-w-0 text-left flex-1"
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textDecoration: soldOut ? 'line-through' : 'none',
            color: soldOut ? 'var(--ui-ink-3)' : undefined,
          }}
        >
          {label}
        </span>
        {/* kcal sits to the LEFT of the price, so the right edge belongs to
            the price (or "Sold out") alone. Calories are nutrition info: they
            never depend on stock, so kcal renders in both states. Price does
            not — hiding an unorderable option's price is a separate,
            pre-existing decision. */}
        {kcal && (
          <span
            style={{
              fontSize: '11px',
              fontWeight: 600,
              fontStyle: 'italic',
              opacity: active ? 0.75 : 0.55,
              whiteSpace: 'nowrap',
              color: soldOut && !active ? 'var(--ui-ink-3)' : undefined,
            }}
          >
            {kcal}
          </span>
        )}
        {soldOut ? (
          <span
            style={{
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--ui-ink-3)',
              whiteSpace: 'nowrap',
            }}
          >
            Sold out
          </span>
        ) : (
          shownPrice > 0 && (
            <span
              style={{
                fontFamily: 'Geist Mono, monospace',
                fontSize: stepper ? '11px' : '12px',
                fontWeight: 700,
                // Not dimmed when inactive: the weight is already 700, and a
                // lighter opacity made it read weaker than the name beside it.
                // Active keeps a slight softening because it is white on the
                // dark selected pill.
                opacity: active ? 0.85 : 1,
                whiteSpace: 'nowrap',
              }}
            >
              +${getNumberFormat(shownPrice)}
            </span>
          )
        )}
        {stepper ? (
          <span className="inline-flex items-center flex-shrink-0">
            {stepButton(HiMinus, `Remove one ${label}`, onDecrement, false)}
            <span
              aria-label={`${label} quantity`}
              style={{
                minWidth: '20px',
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: 800,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {qty}
            </span>
            {stepButton(
              HiPlus,
              `Add one more ${label}`,
              onIncrement,
              !!incrementDisabled || !onIncrement,
            )}
          </span>
        ) : (
          !soldOut && active && <HiOutlineCheck size={14} />
        )}
      </div>
    );
  },
);
OptionPill.displayName = 'OptionPill';

export default OptionPill;
